import { WebGLMap } from "@luciad/ria/view/WebGLMap";
import { CitySource } from "./layers/CitySource";
import { Source, LegendGroup } from "./layers/Source";
import { Navigator } from "./Navigator";
import { getAppConfig } from "./AppConfig";
import { isSafari } from "./BrowserUtil";
import { LayerTreeNode } from "@luciad/ria/view/LayerTreeNode";
import { LayerGroup } from "@luciad/ria/view/LayerGroup";
import { TileSet3DLayer } from "@luciad/ria/view/tileset/TileSet3DLayer";
import { Feature } from "@luciad/ria/model/feature/Feature";
import { MemoryStore } from "@luciad/ria/model/store/MemoryStore";
import { FeatureModel } from "@luciad/ria/model/feature/FeatureModel";
import { FeatureLayer } from "@luciad/ria/view/feature/FeatureLayer";
import { CityLabelLayerPainter } from "./layers/CityLabelLayerPainter";
import { GEOCENTRIC } from "./Reference";

export class ContentManager {
  private readonly _map: WebGLMap;
  private readonly _navigator: Navigator;
  private _citySources: CitySource[];
  private readonly _basemapAddedPromise: Promise<void>;
  private _activeSource: CitySource | null;
  private _activeSourceChangedListener: ActiveSourceChangedListener;
  // private _baseMapOn: boolean = true;
  private _basemapLayers: LayerTreeNode[] = [];
  private _cityLayersFocusPoints: TileSet3DLayer[] = []
  // /**
  //  * The minimum distance between camera and the terrain at which the basemap layers are visible.
  //  */
  // private static BASEMAP_ON_THRESHOLD = 100;

  constructor(map: WebGLMap) {
    this._map = map;
    this._navigator = new Navigator(map);
    getAppConfig().then(
      appConfig => {
        this._citySources = appConfig.citiesSources.map(sourceOptions => new CitySource(sourceOptions))
        this.addCityLayers().then()
      });
    this._basemapAddedPromise = this.addBasemap();
    this._navigator.navigateHome();

    // Reflect browsing history
    window.addEventListener("popstate", (event) => {
      this.setActiveSource(event.state.sourceId, { pushState: false, navigate: true });
    });

    // Change URL when mape moved/rotated
    map.on("MapChange", () => {
      this.setUrlFromMapParams(map, { pushState: true });
      this.updateUrlClosestCity(map);
    });
  }

  private async addBasemap() {
    const appConfig = await getAppConfig();
    const layers = await new Source(appConfig.basemap).layers;

    // Create groups for base layers
    const ortoGroup = new LayerGroup({
      label: "Ortofota"
    });
    const sprCleneniGroup = new LayerGroup({
      label: "Správní členění"
    });

    layers.forEach(layer => {
      this._basemapLayers.push(layer);
      // Insert base layers to the groups
      switch (layer.legendGroup) {
        case LegendGroup.orto:
          ortoGroup.addChild(layer, "bottom")
          break;
        case LegendGroup.sprCleneni:
          sprCleneniGroup.addChild(layer, "bottom")
          break;
        default:
          this._map.layerTree.addChild(layer, "bottom");
      }
    });

    // Add groups to the layer tree
    this._map.layerTree.addChild(ortoGroup, "top");
    this._map.layerTree.addChild(sprCleneniGroup, "top");
  }

  private async addCityLayers() {
    const appConfig = await getAppConfig();
    const layers = appConfig.citiesSources.map(sourceOptions => new CitySource(sourceOptions))
    // Create groups for ogc 3d tiles layers
    const city3dModelGroup = new LayerGroup({
      label: "Modely měst"
    });
    const bimGroup = new LayerGroup({
      label: "BIM"
    });

    layers.forEach(async city => {
      const layers = city.layers;
      await (await layers).reverse().forEach(layer => {
        switch (layer.legendGroup) {
          case LegendGroup.city3d: {
            city3dModelGroup.addChild(layer, "top");
            this._cityLayersFocusPoints.push(layer as TileSet3DLayer)
            break;
          }
          case LegendGroup.bim: {
            bimGroup.addChild(layer, "top");
            break;
          }
          default: {
            this._map.layerTree.addChild(layer, "top");
          }
        }
      }
      )

      // Create feature layer for city labels
      const featuresCityFocus = this._cityLayersFocusPoints.map(layer => {
        let labelPoint = layer.bounds.focusPoint.copy();
        labelPoint.translate3D(0, 0, 100);
        return new Feature(labelPoint, { label: layer.label, id: layer.label })
      })

      const storeCityFocus = new MemoryStore({ data: featuresCityFocus, reference: GEOCENTRIC });
      const modelCityFocus = new FeatureModel(storeCityFocus, { reference: GEOCENTRIC });
      const layerCityFocus = new FeatureLayer(modelCityFocus, { painter: new CityLabelLayerPainter() });
      layerCityFocus.visibleInLayerTree = false;
      layerCityFocus.legendGroup = "notListed";
      this._map.layerTree.addChild(layerCityFocus, "bottom");
    });

    // Add groups to the layer tree
    this._map.layerTree.addChild(bimGroup, "top");
    this._map.layerTree.addChild(city3dModelGroup, "top");
  }

  public async setActiveSource(sourceId?: string, options: {
    /**
     * default true
     */
    animate?: boolean,
    /**
     * default true
     */
    pushState?: boolean,
    /**
     * default true
     */
    navigate?: boolean
  } = { animate: true, pushState: true, navigate: true }) {
    if (options.pushState !== false) {
      history.pushState({ sourceId: sourceId }, "", "?" + (isSafari() ? "webgl=2&" : "") + "obec=" + sourceId);
    }
    if (this._activeSource) {
      // await this.removeSourceFromMap(this._activeSource);
      this._activeSource = null;
    }
    if (!sourceId) {
      return;
    }
    await this._basemapAddedPromise;
    const requestedSource = this._citySources.find(source => source.id === sourceId)!;
    this._activeSource = requestedSource;
    this._activeSourceChangedListener?.onActiveSourceChanged(requestedSource);
    if (!requestedSource) {
      return Promise.resolve();
    }

    if (options.navigate) {
      await this._navigator.navigateTo(requestedSource, options.animate ? options.animate : true).catch(() => null);
    }

    // Activate corresponding city button - highlight the frame
    let button = document.querySelectorAll("button#" + sourceId);
    button[0].classList.add("active");
  }

  public set activeSourceChangedListener(value: ActiveSourceChangedListener) {
    this._activeSourceChangedListener = value;
  }

  // private async removeSourceFromMap(source: Source) {
  //   const layers = await source.layers;
  //   layers.forEach(layer => this._map.layerTree.removeChild(layer));
  // }

  /**
  * Set url after map action (move, rotation) based on map state so that the url can be shared and view reproduced. Includes sourceId (obec) also.
  * @param {WebGLMap} map - main map window
  */
  public async setUrlFromMapParams(map: WebGLMap, options: { pushState?: boolean } = { pushState: true }) {

    let state = map.saveState();
    var queryParams = new URLSearchParams(map.domNode.ownerDocument.location.search);
    let obec = queryParams.get("obec");
    let updatedUrlFromCameraParams =
      window.location.protocol
      + "//" + window.location.host
      + window.location.pathname
      + "?obec=" + obec
      + "&reference=" + state.reference
      + "&x=" + state.transformation3D.eyePointX
      + "&y=" + state.transformation3D.eyePointY
      + "&z=" + state.transformation3D.eyePointZ
      + "&yaw=" + state.transformation3D.yaw
      + "&pitch=" + state.transformation3D.pitch
      + "&roll=" + state.transformation3D.roll;
    
    // Store browsing history
    if (options.pushState !== false) {
      window.history.pushState({ url: updatedUrlFromCameraParams }, "", updatedUrlFromCameraParams);
      history.pushState({ url: updatedUrlFromCameraParams }, "", updatedUrlFromCameraParams);
    }
  }

  /**
   * Finds the closes city in the view for scale bigger than 1 : 150 000
   * @param map 
   * @returns name of the city
   */
  public async updateUrlClosestCity(map: WebGLMap) {
    var queryParams = new URLSearchParams(map.domNode.ownerDocument.location.search);
    let origCityId = queryParams.get("obec");
    let mapScale = map.mapScale;
    var mapState = map.saveState();
    let eyePoint = [mapState.transformation3D.eyePointX, mapState.transformation3D.eyePointY, mapState.transformation3D.eyePointZ];
    let closestPoint: TileSet3DLayer | null = null;
    let minDistance = Infinity;
    let cityFocusPoints = this._cityLayersFocusPoints
    // Updated limited from scale 1 : 150000
    if (mapScale[0] > (1 / 150000) && cityFocusPoints.length > 0) {
      cityFocusPoints.forEach(focusPointLayer => {
        // Calculate distance
        let point = [focusPointLayer.bounds.focusPoint.x, focusPointLayer.bounds.focusPoint.y, focusPointLayer.bounds.focusPoint.z]
        const dist = distance(eyePoint, point);
          // Update the closest point if the current point is closer
          if (dist < minDistance) {
            minDistance = dist;
            closestPoint = focusPointLayer;
          }
      })

      // Get city id from child property value
      const appConfigCitySources = (await getAppConfig()).citiesSources;
      let parentProperties = getParentProperties(appConfigCitySources, "title", closestPoint.label);      
      let actualCityId = parentProperties[0]['id'];
      // console.log(`The closest city: ${actualCityId}, distance: ${parseFloat(minDistance.toFixed(2))}`)

      // Update url
      const url = new URL(window.location.href);
      if (actualCityId != undefined) {
        url.searchParams.set("obec", actualCityId);
        history.replaceState(null, '', url.toString());
      }

      // Deactivate corresponding city button - highlight the frame
      let buttonOrigCity = document.querySelectorAll("button#" + origCityId);
      buttonOrigCity[0].classList.remove("active");
      
      // Activate corresponding city button - highlight the frame
      let buttonActualCity = document.querySelectorAll("button#" + actualCityId);
      buttonActualCity[0].classList.add("active");
    }
  }
}

/**
 * Calculate Euclidean distance between two points
 */
function distance(point1, point2) {
  const dx = point1[0] - point2[0];
  const dy = point1[1] - point2[1];
  const dz = point1[2] - point2[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Get parent properties from child property value
 */
function getParentProperties(data, childProperty, childValue) {
  const parents = [];
  for (const source of data) {
      for (const layer of source.layers) {
          if (layer[childProperty] === childValue) {
              parents.push({
                  id: source.id,
                  title: source.title,
                  iconUrl: source.iconUrl
              });
          }
      }
  }
  return parents;
}

export interface ActiveSourceChangedListener {
  onActiveSourceChanged(source: CitySource): void;
}